import { logger } from "../utils/logger";
/**
 * Enterprise-Grade Role-Based Access Control (RBAC) Service
 *
 * Provides hierarchical role management with proper permission checking
 * and support for multiple roles per user.
 */
// import { logger } from "../utils/logger";
export interface UserRole {
    /** Primary role identifier */
    role: string;
    /** Additional roles the user may have */
    additionalRoles?: string[];
    /** Role permissions */
    permissions?: string[];
    /** Role hierarchy level (higher = more permissions) */
    hierarchyLevel?: number;
}
export interface RoleHierarchy {
    [role: string]: {
        level: number;
        permissions: string[];
        inherits?: string[];
    };
}
class RoleService {
    // Enterprise role hierarchy - higher numbers = more permissions
    private static readonly ROLE_HIERARCHY: RoleHierarchy = {
        'super_admin': {
            level: 100,
            permissions: ['*'], // All permissions
            inherits: ['admin', 'manager', 'user']
        },
        'admin': {
            level: 80,
            permissions: [
                'admin:read', 'admin:write', 'admin:delete',
                'user:read', 'user:write', 'user:delete',
                'content:read', 'content:write', 'content:delete',
                'reports:read', 'reports:write'
            ],
            inherits: ['manager', 'user']
        },
        'manager': {
            level: 60,
            permissions: [
                'user:read', 'user:write',
                'content:read', 'content:write',
                'reports:read'
            ],
            inherits: ['user']
        },
        'instructor': {
            level: 50,
            permissions: [
                'course:read', 'course:write',
                'student:read', 'student:write',
                'content:read', 'content:write'
            ],
            inherits: ['user']
        },
        'coordinator': {
            level: 45,
            permissions: [
                'event:read', 'event:write',
                'student:read', 'student:write',
                'content:read'
            ],
            inherits: ['user']
        },
        'supervisor': {
            level: 40,
            permissions: [
                'member:read', 'member:write',
                'reports:read'
            ],
            inherits: ['user']
        },
        'user': {
            level: 20,
            permissions: [
                'profile:read', 'profile:write',
                'content:read'
            ]
        },
        'guest': {
            level: 10,
            permissions: [
                'content:read'
            ]
        }
    };
    /**
     * Check if a user has any of the required roles
     * @param user - User object with role information
     * @param requiredRoles - Array of roles to check against
     * @returns True if user has at least one of the required roles
     */
    public static userHasRole(user: any, requiredRoles: string[]): boolean {
        if (!user || !requiredRoles || requiredRoles.length === 0) {
            return true; // No restrictions
        }
        if (!user.role) {
            return false; // User has no role
        }
        try {
            const userRoles = this.getUserRoles(user);
            // Check if user has any of the required roles
            return requiredRoles.some(requiredRole => userRoles.some(userRole => this.rolesMatch(userRole, requiredRole)));
        }
        catch (error) {
            logger.error('[RoleService] Error checking user roles:', error, {});
            return false; // Fail securely
        }
    }
    /**
     * Check if a user has any of the required permissions
     * @param user - User object with role information
     * @param requiredPermissions - Array of permissions to check against
     * @returns True if user has at least one of the required permissions
     */
    public static userHasAnyPermission(user: any, requiredPermissions: string[]): boolean {
        if (!user || !requiredPermissions || requiredPermissions.length === 0) {
            return true; // No restrictions
        }
        try {
            const userPermissions = this.getUserPermissions(user);
            // Check if user has any of the required permissions
            return requiredPermissions.some(permission => userPermissions.includes(permission) || userPermissions.includes('*'));
        }
        catch (error) {
            logger.error('[RoleService] Error checking user permissions:', error, {});
            return false; // Fail securely
        }
    }
    /**
     * Check if a user has all of the required permissions
     * @param user - User object with role information
     * @param requiredPermissions - Array of permissions to check against
     * @returns True if user has all of the required permissions
     */
    public static userHasAllPermissions(user: any, requiredPermissions: string[]): boolean {
        if (!user || !requiredPermissions || requiredPermissions.length === 0) {
            return true; // No restrictions
        }
        try {
            const userPermissions = this.getUserPermissions(user);
            // Check if user has all required permissions
            return requiredPermissions.every(permission => userPermissions.includes(permission) || userPermissions.includes('*'));
        }
        catch (error) {
            logger.error('[RoleService] Error checking user permissions:', error, {});
            return false; // Fail securely
        }
    }
    /**
     * Check if a user has a specific permission
     * @param user - User object with role information
     * @param permission - Permission to check
     * @returns True if user has the permission
     */
    public static userHasPermission(user: any, permission: string): boolean {
        if (!user || !permission) {
            return false;
        }
        const userRoles = this.getUserRoles(user);
        return userRoles.some(role => {
            const roleConfig = this.ROLE_HIERARCHY[role];
            if (!roleConfig)
                return false;
            // Check for wildcard permission
            if (roleConfig.permissions.includes('*')) {
                return true;
            }
            // Check for exact permission
            if (roleConfig.permissions.includes(permission)) {
                return true;
            }
            // Check for wildcard permissions (e.g., 'admin:*' matches 'admin:read')
            return roleConfig.permissions.some(rolePermission => {
                if (rolePermission.endsWith(':*')) {
                    const prefix = rolePermission.slice(0, -2);
                    return permission.startsWith(prefix + ':');
                }
                return false;
            });
        });
    }
    /**
     * Get all roles for a user (primary + additional)
     * @param user - User object
     * @returns Array of role strings
     */
    public static getUserRoles(user: any): string[] {
        if (!user)
            return [];
        try {
            const roles: string[] = [];
            // Add primary role with safe fallback
            if (user.role && typeof user.role === 'string') {
                roles.push(user.role.toLowerCase().trim());
            }
            // Add additional roles with safe fallback
            if (user.additionalRoles && Array.isArray(user.additionalRoles)) {
                const additionalRoles = user.additionalRoles
                    .filter((role: any) => typeof role === 'string')
                    .map((role: string) => role.toLowerCase().trim());
                roles.push(...additionalRoles);
            }
            // Add inherited roles
            const inheritedRoles = this.getInheritedRoles(roles);
            roles.push(...inheritedRoles);
            // Remove duplicates and empty strings
            return [...new Set(roles.filter(role => role.length > 0))];
        }
        catch (error) {
            logger.error('[RoleService] Error getting user roles:', error, {});
            return []; // Return empty array on error
        }
    }
    /**
     * Get the highest hierarchy level for a user
     * @param user - User object
     * @returns Highest hierarchy level number
     */
    public static getUserHierarchyLevel(user: any): number {
        const userRoles = this.getUserRoles(user);
        let maxLevel = 0;
        userRoles.forEach(role => {
            const roleConfig = this.ROLE_HIERARCHY[role];
            if (roleConfig && roleConfig.level > maxLevel) {
                maxLevel = roleConfig.level;
            }
        });
        return maxLevel;
    }
    /**
     * Check if user's role level meets minimum requirement
     * @param user - User object
     * @param minimumLevel - Minimum hierarchy level required
     * @returns True if user meets minimum level
     */
    public static userMeetsMinimumLevel(user: any, minimumLevel: number): boolean {
        return this.getUserHierarchyLevel(user) >= minimumLevel;
    }
    /**
     * Get all permissions for a user
     * @param user - User object
     * @returns Array of permission strings
     */
    public static getUserPermissions(user: any): string[] {
        try {
            const userRoles = this.getUserRoles(user);
            const permissions = new Set<string>();
            userRoles.forEach(role => {
                const roleConfig = this.ROLE_HIERARCHY[role];
                if (roleConfig && Array.isArray(roleConfig.permissions)) {
                    roleConfig.permissions.forEach(permission => {
                        if (typeof permission === 'string') {
                            permissions.add(permission);
                        }
                    });
                }
            });
            return Array.from(permissions);
        }
        catch (error) {
            logger.error('[RoleService] Error getting user permissions:', error, {});
            return []; // Return empty array on error
        }
    }
    /**
     * Get role configuration
     * @param role - Role name
     * @returns Role configuration or null if not found
     */
    public static getRoleConfig(role: string): {
        level: number;
        permissions: string[];
        inherits?: string[];
    } | null {
        return this.ROLE_HIERARCHY[role.toLowerCase()] || null;
    }
    /**
     * Get all available roles
     * @returns Array of all role names
     */
    public static getAllRoles(): string[] {
        return Object.keys(this.ROLE_HIERARCHY);
    }
    /**
     * Check if two roles match (handles case-insensitive comparison)
     * @param role1 - First role
     * @param role2 - Second role
     * @returns True if roles match
     */
    private static rolesMatch(role1: string, role2: string): boolean {
        return role1.toLowerCase() === role2.toLowerCase();
    }
    /**
     * Get inherited roles for a given set of roles
     * @param roles - Array of role names
     * @returns Array of inherited role names
     */
    private static getInheritedRoles(roles: string[]): string[] {
        const inheritedRoles = new Set<string>();
        roles.forEach(role => {
            const roleConfig = this.ROLE_HIERARCHY[role];
            if (roleConfig && roleConfig.inherits) {
                roleConfig.inherits.forEach(inheritedRole => {
                    inheritedRoles.add(inheritedRole);
                });
            }
        });
        return Array.from(inheritedRoles);
    }
    /**
     * Validate role hierarchy configuration
     * @returns True if configuration is valid
     */
    public static validateRoleHierarchy(): boolean {
        try {
            const roles = Object.keys(this.ROLE_HIERARCHY);
            for (const role of roles) {
                const config = this.ROLE_HIERARCHY[role];
                // Check required properties
                if (typeof config.level !== 'number' || !Array.isArray(config.permissions)) {
                    logger.error("Error occurred", `[RoleService] Invalid configuration for role: ${role}`, {});
                    return false;
                }
                // Check inheritance references
                if (config.inherits) {
                    for (const inheritedRole of config.inherits) {
                        if (!this.ROLE_HIERARCHY[inheritedRole]) {
                            logger.error("Error occurred", `[RoleService] Role ${role} inherits from non-existent role: ${inheritedRole}`, {});
                            return false;
                        }
                    }
                }
            }
            logger.debug('[RoleService] Role hierarchy validation passed');
            return true;
        }
        catch (error) {
            logger.error('[RoleService] Role hierarchy validation failed:', error, {});
            return false;
        }
    }
}
// Validate role hierarchy on module load
RoleService.validateRoleHierarchy();
export default RoleService;
